import React from 'react';
import { BrowserRouter, Routes, Route, Outlet, Navigate } from 'react-router-dom';
import { McpWaitlistProvider } from './contexts/McpWaitlistContext';
import { CustomWorkflowBuilder } from './pages/CustomWorkflowBuilder';
import { PartnerVotesProvider } from './contexts/PartnerVotesContext';
import { Partners } from './pages/Partners';
import { PartnerProfile } from './pages/PartnerProfile';
import { ThemeProvider } from 'next-themes';
import { SiteSchema } from './components/SiteSchema';
import { ScrollManager } from './components/ScrollManager';
import { DgptHeader } from './components/dgpt/DgptHeader';
import { DgptFooter } from './components/dgpt/DgptFooter';
import { Home } from './pages/Home';
import { SampleMemo } from './pages/SampleMemo';
import { About } from './pages/About';
import { ToolsDirectory } from './pages/ToolsDirectory';
import { ToolPage } from './pages/ToolPage';
import { NotFound } from './pages/NotFound';
import { Agents } from './pages/Agents';
import { Solutions } from './pages/Solutions';
import { VentureCapital } from './pages/VentureCapital';
import { Enterprise } from './pages/Enterprise';
import { Security } from './pages/Security';
import { Pricing } from './pages/Pricing';
import { SolutionPage } from './pages/SolutionPage';
import { Mcp } from './pages/Mcp';
type AppProps = {
  /**
   * Design preview only: show sample usage counts (labelled "Illustrative count")
   * on tools without a verified count. Must stay false in production.
   */
  showIllustrativeCounts?: boolean;
  /**
   * Design preview only: simulate the MCP waitlist outcome without sending anything.
   * "live" submits to the real endpoint. Must stay "live" in production.
   */
  mcpWaitlistPreview?: 'live' | 'success' | 'error';
};
export function App({
  showIllustrativeCounts = false,
  mcpWaitlistPreview = 'live'
}: AppProps) {
  return <ThemeProvider attribute="class" defaultTheme="light" enableSystem data-id="element-0" data-mp="nvartrdc3fg4">
      <BrowserRouter data-id="element-1" data-mp="stkj3qvxtvwg">
        <McpWaitlistProvider preview={mcpWaitlistPreview} data-id="element-2" data-mp="sssr0q1jugax">
        <ScrollManager data-id="element-3" data-mp="35ozexlizgwa" />
        <SiteSchema data-id="element-4" data-mp="356wji4cy4fe" />
        {/* Top padding reserves space for the fixed header (h-16 / lg:h-[72px]). */}
        <div className="min-h-screen w-full bg-ink pt-16 font-sans text-text-primary antialiased lg:pt-[72px]" data-id="element-5" data-mp="3uc9inv5c9kir">
          <DgptHeader data-id="element-6" data-mp="d2l9h7r2ob2y" />
          <Routes data-id="element-7" data-mp="85l12q6r2w8o">
            <Route path="/" element={<Home data-id="element-9" data-mp="86w7kxrkyi3h" />} data-id="element-8" data-mp="851n1kk80rzx" />
            <Route path="/agents" element={<Agents data-id="element-11" data-mp="3gleaym6kt9gr" />} data-id="element-10" data-mp="86ckz3qzj5j4" />
            <Route path="/solutions" element={<Solutions data-id="element-13" data-mp="3glwfs77b0w0a" />} data-id="element-12" data-mp="3gklbm69y5m8n" />
            <Route path="/solutions/custom-workflow-builder" element={<CustomWorkflowBuilder data-id="element-15" data-mp="3bnmedeuzkz68" />} data-id="element-14" data-mp="3gj1w4hw47f7x" />
            {/* Alternate solution URLs redirect to the existing pages. */}
            <Route path="/solutions/venture-capital" element={<Navigate to="/venture-capital" replace data-id="element-17" data-mp="3ljxu6etsc75f" />} data-id="element-16" data-mp="3qciodhglsm9h" />
            <Route path="/solutions/private-equity" element={<Navigate to="/solutions/invest" replace data-id="element-19" data-mp="2xu3ku78od0px" />} data-id="element-18" data-mp="3liefutrow66a" />
            <Route path="/solutions/acquisitions" element={<Navigate to="/solutions/acquire" replace data-id="element-21" data-mp="2suqxzmhsztnc" />} data-id="element-20" data-mp="2xsk289zjqdkz" />
            <Route path="/solutions/procurement" element={<Navigate to="/solutions/buy" replace data-id="element-23" data-mp="18cq57il9mcrv" />} data-id="element-22" data-mp="18drjdvxkvfdt" />
            <Route path="/solutions/grants" element={<Navigate to="/solutions/select" replace data-id="element-25" data-mp="1d5j9y6gqhl3y" />} data-id="element-24" data-mp="1d4hp5a9x1k91" />
            <Route path="/solutions/:slug" element={<SolutionPage data-id="element-27" data-mp="ygmsxjwp8xr7" />} data-id="element-26" data-mp="yflk7m35oncn" />
            <Route path="/mcp" element={<Mcp data-id="element-29" data-mp="13eoeqclifb6d" />} data-id="element-28" data-mp="yhenqsih2rgf" />
            <Route path="/venture-capital" element={<VentureCapital data-id="element-31" data-mp="13ey3xxyqdeb0" />} data-id="element-30" data-mp="13dws3r9w4ns8" />
            <Route path="/enterprise" element={<Enterprise data-id="element-33" data-mp="1r5aktybyeldq" />} data-id="element-32" data-mp="1r497tobdnotd" />
            <Route path="/security" element={<Security data-id="element-35" data-mp="1w3c1kclwq8m1" />} data-id="element-34" data-mp="1r526s343ha0y" />
            <Route path="/pricing" element={<Pricing data-id="element-37" data-mp="1w2tz2y8niy5o" />} data-id="element-36" data-mp="1w3lua7yc6tvf" />
            <Route path="/sample-memo" element={<SampleMemo data-id="element-39" data-mp="1i444bp9b1akh" />} data-id="element-38" data-mp="1w0zp2eyx4qgd" />
            <Route path="/about" element={<About data-id="element-41" data-mp="1i5x9f049rmqd" />} data-id="element-40" data-mp="1i31b3aoiew1e" />
            <Route path="/tools" element={<ToolsDirectory data-id="element-43" data-mp="1m44sw2q4b259" />} data-id="element-42" data-mp="1m4wbmcym95e4" />
            <Route path="/tools/:slug" element={<ToolPage showIllustrativeCounts={showIllustrativeCounts} data-id="element-45" data-mp="6onpydl54up2" />} data-id="element-44" data-mp="1m7925eifwo2s" />
            <Route element={<PartnerVotesProvider data-id="element-47" data-mp="ar29kzwmvyhe">
                  <Outlet data-id="element-48" data-mp="anwjbhc9qqk3" />
                </PartnerVotesProvider>} data-id="element-46" data-mp="6qs3ln8ud82j">
              <Route path="/partners" element={<Partners data-id="element-50" data-mp="m6p3afeoh4on" />} data-id="element-49" data-mp="qygkw0fuw02n" />
              <Route path="/partners/:slug" element={<PartnerProfile data-id="element-52" data-mp="m4cblaj4bfye" />} data-id="element-51" data-mp="m5e0aht240yl" />
            </Route>
            <Route path="*" element={<NotFound data-id="element-54" data-mp="h7vnuhftz3lu" />} data-id="element-53" data-mp="h6kn6qhombyy" />
          </Routes>
          <DgptFooter data-id="element-55" data-mp="c897w08mb99c" />
        </div>
        </McpWaitlistProvider>
      </BrowserRouter>
    </ThemeProvider>;
}